import { useState } from 'react'
import { toast } from 'sonner'
import { User, OvertimeRequest, OvertimeStatus } from '@/types'
import { OvertimeStatus as OvertimeStatusEnum, UserRole } from '@/types/enums'
import { supabase } from '@/integrations/supabase/client'

type SetOvertimeRequestsAction = React.Dispatch<React.SetStateAction<OvertimeRequest[]>>

/**
 * Hook for handling overtime approval and rejection logic.
 * Provides functions for standard and VP (Super Admin) approval, as well as rejection.
 */
export function useApprovalHandlers(
  user: User | null, 
  setOvertimeRequests: SetOvertimeRequestsAction,
  fetchRequests: () => Promise<boolean>
) {
  const [isApproving, setIsApproving] = useState(false)
  const [isRejecting, setIsRejecting] = useState(false)
  
  /**
   * Update local state immediately after a successful request update
   */
  const updateLocalState = (requestId: string, status: OvertimeStatus, comment: string) => {
    setOvertimeRequests(prev => 
      prev.map(req => 
        req.id === requestId
          ? {
              ...req,
              status,
              approver_id: user?.id,
              approver_name: user?.name,
              approver_comment: comment,
              updated_at: new Date().toISOString()
            }
          : req
      ).filter(req => req.id !== requestId) // Remove the approved/rejected request
    );
  };

  /**
   * Approve an overtime request. Handles both standard and VP approval logic.
   * @param requestId - The ID of the overtime request to approve
   * @param approvalComment - The approval comment
   * @returns Promise<boolean> indicating success
   */
  const approveOvertimeRequest = async (
    requestId: string, 
    approvalComment: string
  ): Promise<boolean> => {
    if (!user) {
      toast.error("You must be logged in to approve requests");
      return false;
    }

    if (!approvalComment?.trim()) {
      return false;
    }

    try {
      setIsApproving(true);
      
      // Basic request validation
      if (!requestId) {
        toast.error("Invalid request ID");
        return false;
      }

      // First fetch the request to check permissions
      const { data: request, error: fetchError } = await supabase
        .from('overtime_requests')
        .select('*')
        .eq('id', requestId)
        .single();

      if (fetchError || !request) {
        toast.error("Request not found");
        return false;
      }

      // Handle super admin approvals
      if (user.role === UserRole.SUPER_ADMIN) {
        // Super admin can approve any request that requires VP approval or was created by plant manager
        const canApprove = request.vp_approval_required || 
                          request.created_by_role === UserRole.PLANT_MANAGER ||
                          request.status === OvertimeStatusEnum.PENDING_VP_APPROVAL;
        
        if (!canApprove) {
          toast.error("This request doesn't require super admin approval");
          return false;
        }
      }

      // Determine the next status based on current status and user role
      let nextStatus: OvertimeStatus = OvertimeStatusEnum.APPROVED;
      
      if (user.role === UserRole.DEPARTMENT_HEAD) {
        // Department Head can ONLY approve superintendent requests with pending_dh_approval
        if (request.status === OvertimeStatusEnum.PENDING_DH_APPROVAL && 
            request.created_by_role?.toLowerCase() === UserRole.SUPERINTENDENT) {
          nextStatus = OvertimeStatusEnum.PENDING_PM_APPROVAL;
        } else {
          toast.error("You don't have permission to approve this request");
          return false;
        }
      } else if (user.role === UserRole.PLANT_MANAGER) {
        // Plant manager can approve pending_pm_approval (from superintendent workflow)
        // or legacy pending requests from department heads
        const canApprove = (request.status === OvertimeStatusEnum.PENDING_PM_APPROVAL) ||
                          (request.created_by_role === UserRole.DEPARTMENT_HEAD && 
                           !request.vp_approval_required &&
                           request.status === OvertimeStatusEnum.PENDING);
        
        if (!canApprove) {
          toast.error("You don't have permission to approve this request");
          return false;
        }
        nextStatus = OvertimeStatusEnum.APPROVED;
      } else if (user.role === UserRole.HR) {
        // HR can only approve superintendent requests with pending_dh_approval status
        // and changes status to pending_pm_approval (takes over Department Head role)
        const canApprove = (request.status === OvertimeStatusEnum.PENDING_DH_APPROVAL && 
                           request.created_by_role?.toLowerCase() === UserRole.SUPERINTENDENT);
        
        if (!canApprove) {
          toast.error("You don't have permission to approve this request");
          return false;
        }
        nextStatus = OvertimeStatusEnum.PENDING_PM_APPROVAL;
      } else if (user.role === UserRole.SUPER_ADMIN) {
        // Super admin can approve:
        // 1. pending_vp_approval requests (VP threshold exceeded)
        // 2. pending requests created by plant_manager
        const canApprove = (request.status === OvertimeStatusEnum.PENDING_VP_APPROVAL) ||
                          (request.status === OvertimeStatusEnum.PENDING && request.created_by_role === UserRole.PLANT_MANAGER);
        
        if (!canApprove) {
          toast.error("You don't have permission to approve this request");
          return false;
        }
        nextStatus = OvertimeStatusEnum.APPROVED;
      }
      
      const { error } = await supabase
        .from('overtime_requests')
        .update({
          status: nextStatus,
          approver_id: user.id,
          approver_name: user.name,
          approver_comment: approvalComment,
          updated_at: new Date().toISOString()
        })
        .eq('id', requestId);

      if (error) {
        if (error.code === 'PGRST116') {
          toast.error("Request not found or already processed");
          return false;
        }
        throw error;
      }

      // Update local state immediately
      updateLocalState(requestId, nextStatus, approvalComment);
      
      // Show success message based on next status
      if (nextStatus === OvertimeStatusEnum.PENDING_PM_APPROVAL) {
        if (user.role === UserRole.HR) {
          toast.success("Request approved by HR. Sent to Plant Manager for final approval.");
        } else {
          toast.success("Request approved. Sent to Plant Manager for final approval.");
        }
      } else if (nextStatus === OvertimeStatusEnum.APPROVED) {
        toast.success("Request approved successfully");
      } else {
        toast.success("Request processed successfully");
      }
      
      // Refresh the requests list in the background
      fetchRequests().catch(() => {
      });
      
      return true;
    } catch (error) {
      if (error instanceof Error && error.message.includes('network')) {
        toast.error("Network error - please check your connection");
      } else {
        toast.error("Failed to approve request. Please try again.");
      }
      return false;
    } finally {
      setIsApproving(false);
    }
  };
  
  /**
   * Reject an overtime request.
   * @param requestId - The ID of the overtime request to reject
   * @param comment - The rejection reason
   * @returns Promise<boolean> indicating success
   */
  const rejectOvertimeRequest = async (
    requestId: string, 
    comment: string
  ): Promise<boolean> => {
    if (!user) {
      toast.error("You must be logged in to reject requests");
      return false;
    }
    
    if (!comment?.trim()) {
      return false;
    }
    
    try {
      setIsRejecting(true);
      
      // First fetch the request to validate its state
      const { data: request, error: fetchError } = await supabase
        .from('overtime_requests')
        .select('*')
        .eq('id', requestId)
        .single();

      if (fetchError || !request) {
        toast.error("Request not found");
        return false;
      }

      if (request.status === OvertimeStatusEnum.REJECTED) {
        toast.error("This request has already been rejected");
        return false;
      }
      
      // Update the request in Supabase
      const { error } = await supabase
        .from('overtime_requests')
        .update({
          status: OvertimeStatusEnum.REJECTED,
          updated_at: new Date().toISOString(),
          approver_id: user.id,
          approver_name: user.name,
          approver_comment: comment
        })
        .eq('id', requestId);
      
      if (error) {
        if (error.code === 'PGRST116') {
          toast.error("Request not found or already processed");
          return false;
        }
        throw error;
      }
      
      // Update local state immediately
      updateLocalState(requestId, OvertimeStatusEnum.REJECTED, comment);
      
      // Show success message
      toast.success("Request rejected successfully");
      
      // Refresh the requests list in the background
      fetchRequests().catch(() => {
      });
      
      return true;
    } catch (error) {
      if (error instanceof Error && error.message.includes('network')) {
        toast.error("Network error - please check your connection");
      } else {
        toast.error("Failed to reject request. Please try again.");
      }
      return false;
    } finally {
      setIsRejecting(false);
    }
  };
  
  /**
   * Approve an overtime request as a VP (Super Admin).
   * @param requestId - The ID of the overtime request to approve
   * @param comment - The approval comment
   * @returns Promise<boolean> indicating success
   */
  const approveOvertimeRequestAsVP = async (
    requestId: string,
    comment: string
  ): Promise<boolean> => {
    if (!user || user.role !== UserRole.SUPER_ADMIN) {
      toast.error("Only super admins can perform VP approvals");
      return false;
    }

    if (!comment?.trim()) {
      return false;
    }

    try {
      setIsApproving(true);

      // First fetch the request to check if it still needs VP approval
      const { data: request, error: fetchError } = await supabase
        .from('overtime_requests')
        .select('*')
        .eq('id', requestId)
        .single();

      if (fetchError || !request) {
        toast.error("Request not found");
        return false;
      }

      const needsVPApproval = request.vp_approval_required || request.status === OvertimeStatusEnum.PENDING_VP_APPROVAL;
      if (!needsVPApproval) {
        toast.error("This request doesn't require VP approval");
        return false;
      }

      const { error } = await supabase
        .from('overtime_requests')
        .update({
          status: OvertimeStatusEnum.APPROVED,
          approver_id: user.id,
          approver_name: user.name,
          approver_comment: comment,
          updated_at: new Date().toISOString()
        })
        .eq('id', requestId);

      if (error) {
        if (error.code === 'PGRST116') {
          toast.error("Request not found or already processed");
          return false;
        }
        throw error;
      }

      // Update local state immediately
      updateLocalState(requestId, OvertimeStatusEnum.APPROVED, comment);
      
      // Show success message
      toast.success("Request approved successfully");
      
      // Refresh the requests list in the background
      fetchRequests().catch(() => {
      });
      
      return true;
    } catch (error) {
      if (error instanceof Error && error.message.includes('network')) {
        toast.error("Network error - please check your connection");
      } else {
        toast.error("Failed to approve request. Please try again.");
      }
      return false;
    } finally {
      setIsApproving(false);
    }
  };

  return {
    approveOvertimeRequest,
    rejectOvertimeRequest,
    approveOvertimeRequestAsVP,
    isApproving,
    isRejecting
  };
}
